// src/pages/Checkout.js    
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../services/supabaseClient';
import {
  Box,
  Typography,
  Alert,
  Paper,
  Stepper,
  Step,
  StepLabel
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';

// Import the new components
import DeliveryInfo from '../components/checkout/DeliveryInfo';
import PaymentMethod from '../components/checkout/PaymentMethod';
import OrderConfirmation from '../components/checkout/OrderConfirmation';

const Checkout = () => {
  const { user } = useAuth();
  const { cartItems, clearCart, cartCount, paymentMethod, setPaymentMethod } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [paymentScreenshot, setPaymentScreenshot] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [orderData, setOrderData] = useState({
    fullName: '',
    address: '',
    phone: '',
    additionalNotes: ''
  });

  // Shipping related state
  const [shippingPrices, setShippingPrices] = useState([]);
  const [selectedGovernorate, setSelectedGovernorate] = useState('');
  const [shippingPrice, setShippingPrice] = useState(0);
  const [loadingShipping, setLoadingShipping] = useState(true);
  const [productShippingInfo, setProductShippingInfo] = useState({});

  // Calculate total amount
  const totalAmount = cartItems.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );

  // Check if all items have free shipping
  const hasAllFreeShipping = useMemo(() => {
    if (cartItems.length === 0) return false;
    return cartItems.every(item => productShippingInfo[item.id]?.free_shipping === true);
  }, [cartItems, productShippingInfo]);

  // Calculate final shipping price (0 if all items have free shipping)
  const finalShippingPrice = hasAllFreeShipping ? 0 : shippingPrice;

  // Calculate final total including shipping
  const finalTotal = totalAmount + finalShippingPrice;

  // Fetch product shipping information
  const fetchProductShippingInfo = useCallback(async () => {
    if (cartItems.length === 0) return;
    
    try {
      const productIds = [...new Set(cartItems.map(item => item.id))];
      const { data, error } = await supabase
        .from('products')
        .select('id, free_shipping')
        .in('id', productIds);
      
      if (error) {
        console.error('Error fetching product shipping info:', error);
        return;
      }
      
      const shippingInfoMap = {};
      data.forEach(product => {
        shippingInfoMap[product.id] = { free_shipping: product.free_shipping };
      });
      
      setProductShippingInfo(shippingInfoMap);
    } catch (err) {
      console.error('Error fetching product shipping info:', err);
    }
  }, [cartItems]);

  useEffect(() => {
    // Fetch user profile data if available
    const fetchUserProfile = async () => {
      if (user?.id) {
        const { data, error } = await supabase
          .from('profiles')
          .select('full_name, phone_number')
          .eq('id', user.id)
          .single();
          
        if (data && !error) {
          setOrderData(prev => ({
            ...prev,
            fullName: data.full_name || '',
            phone: data.phone_number || ''
          }));
        }
      }
    };
    
    fetchUserProfile();
  }, [user]);

  // Fetch shipping prices and product shipping info
  useEffect(() => {
    const fetchShippingPrices = async () => {
      try {
        const { data, error } = await supabase
          .from('shipping_prices')
          .select('*')
          .order('governorate_name_ar');
        
        if (error) {
          console.error('Error fetching shipping prices:', error);
          return;
        }
        
        setShippingPrices(data || []);
        
        // If shipping info was passed from cart, set it
        if (location.state?.shippingInfo) {
          setSelectedGovernorate(location.state.shippingInfo.id);
          // Use the shipping price from location state if available, otherwise from the fetched data
          if (location.state.hasAllFreeShipping) {
            setShippingPrice(0);
          } else {
            setShippingPrice(location.state.shippingInfo.shipping_price || 0);
          }
        }
      } catch (err) {
        console.error('Error fetching shipping prices:', err);
      } finally {
        setLoadingShipping(false);
      }
    };

    fetchShippingPrices();
    fetchProductShippingInfo();
  }, [location.state, fetchProductShippingInfo]);

  // Handle governorate selection
  const handleGovernorateChange = (event) => {
    const governorateId = event.target.value;
    setSelectedGovernorate(governorateId);
    
    if (governorateId) {
      const selectedPrice = shippingPrices.find(price => price.id === governorateId);
      // Set shipping price to 0 if all items have free shipping, otherwise use the actual price
      setShippingPrice(hasAllFreeShipping ? 0 : (selectedPrice ? selectedPrice.shipping_price : 0));
    } else {
      setShippingPrice(0);
    }
  };

  const handlePaymentMethodChange = (event) => {
    setPaymentMethod(event.target.value);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setOrderData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const validateDeliveryInfo = () => {
    if (!orderData.fullName || !orderData.address || !orderData.phone) {
      setError('يرجى إكمال جميع حقول بيانات التوصيل المطلوبة');
      return false;
    }
    
    if (!selectedGovernorate) {
      setError('يرجى اختيار المحافظة');
      return false;
    }
    
    if (!/^\d{11}$/.test(orderData.phone.replace(/\s/g, ''))) {
      setError('يرجى إدخال رقم هاتف صحيح مكون من 11 رقم');
      return false;
    }
    
    setError('');
    return true;
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Validate payment method specific requirements
      if (paymentMethod === 'vodafone_cash' && !paymentScreenshot) {
        throw new Error('الرجاء رفع صورة إيصال الدفع');
      }

      // Verify stock availability
      for (const item of cartItems) {
        const { data: productColorSize, error: stockError } = await supabase
          .from('product_color_sizes')
          .select('stock')
          .eq('product_color_id', item.colorId)
          .eq('size', item.size)
          .single();

        if (stockError) {
          throw new Error(`خطأ في التحقق من المخزون: ${stockError.message}`);
        }

        if (!productColorSize || productColorSize.stock < item.quantity) {
          throw new Error(`المنتج ${item.name_ar || item.name} مقاس ${item.size} غير متوفر بالكمية المطلوبة`);
        }
      }

      let paymentScreenshotFilename = null;
      
      // Upload payment screenshot if vodafone cash
      if (paymentMethod === 'vodafone_cash' && paymentScreenshot) {
        const fileExt = paymentScreenshot.name.split('.').pop();
        const fileName = `${user.id}/${Date.now()}.${fileExt}`;
        const { error: uploadError } = await supabase.storage
          .from('payment-screenshots')
          .upload(fileName, paymentScreenshot);

        if (uploadError) throw uploadError;
        paymentScreenshotFilename = fileName;
      }

      // Get selected governorate info
      const selectedGovInfo = shippingPrices.find(price => price.id === selectedGovernorate);
      const originalShippingPrice = selectedGovInfo?.shipping_price || 0;

      // Create order with shipping info
      const { data: order, error: orderError } = await supabase
        .from('orders')
        .insert([
          {
            user_id: user.id,
            total_amount: finalTotal,
            payment_method: paymentMethod,
            payment_screenshot: paymentScreenshotFilename,
            status: 'pending',
            shipping_address: JSON.stringify({
              fullName: orderData.fullName,
              address: orderData.address,
              phone: orderData.phone,
              notes: orderData.additionalNotes,
              governorate: selectedGovInfo?.governorate_name_ar || '',
              shippingPrice: finalShippingPrice,
              originalShippingPrice: originalShippingPrice,
              hasAllFreeShipping: hasAllFreeShipping
            })
          }
        ])
        .select()
        .single();

      if (orderError) throw orderError;

      // Create order items
      const orderItems = cartItems.map((item) => ({
        order_id: order.id,
        product_id: item.id,
        product_color_id: item.colorId,
        name: item.name_ar || item.name,
        color: item.colorName || '',
        size: item.size || '',
        quantity: item.quantity, 
        image_url: item.image || '',
        price_at_purchase: item.price
      }));

      const { error: itemsError } = await supabase
        .from('order_items')
        .insert(orderItems);

      if (itemsError) throw itemsError;

      // Update stock for each item
      for (const item of cartItems) {
        const { error: updateStockError } = await supabase.rpc('decrease_product_stock', {
          p_product_color_id: item.colorId,
          p_size: item.size,
          p_quantity: item.quantity
        });

        if (updateStockError) {
          throw new Error(`خطأ في تحديث المخزون: ${updateStockError.message}`);
        }
      }

      clearCart();
      
      let successMessage = paymentMethod === 'vodafone_cash' 
        ? 'تم تأكيد الطلب بنجاح! سيتم مراجعة الدفع وإعلامك بالتحديثات' 
        : 'تم تأكيد الطلب بنجاح! سيتم التواصل معك قريباً لتأكيد الطلب';
      
      if (hasAllFreeShipping && originalShippingPrice > 0) {
        successMessage += ` | وفرت ${originalShippingPrice.toFixed(2)} جم من رسوم الشحن!`;
      }
      
      setSuccess(successMessage);
      setTimeout(() => navigate('/profile'), 3000);
    } catch (error) {
      console.error('Checkout error:', error);
      setError(error.message || 'حدث خطأ أثناء تأكيد الطلب');
    } finally {
      setLoading(false);
    }
  };

  // Fixed steps array - now "بيانات التوصيل" is first (step 1 from right to left)
  const steps = ['بيانات التوصيل', 'طريقة الدفع', 'تأكيد الطلب'];

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <DeliveryInfo
            orderData={orderData}
            handleInputChange={handleInputChange}
            shippingPrices={shippingPrices}
            selectedGovernorate={selectedGovernorate}
            handleGovernorateChange={handleGovernorateChange}
            shippingPrice={shippingPrice}
            finalShippingPrice={finalShippingPrice}
            hasAllFreeShipping={hasAllFreeShipping}
            loadingShipping={loadingShipping}
            validateDeliveryInfo={validateDeliveryInfo}
            onNext={handleNext}
          />
        );
      case 1:
        return (
          <PaymentMethod
            paymentMethod={paymentMethod}
            handlePaymentMethodChange={handlePaymentMethodChange}
            paymentScreenshot={paymentScreenshot}
            setPaymentScreenshot={setPaymentScreenshot}
            finalTotal={finalTotal}
            error={error}
            setError={setError}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 2:
        return (
          <OrderConfirmation
            cartItems={cartItems}
            totalAmount={totalAmount}
            shippingPrice={finalShippingPrice}
            originalShippingPrice={shippingPrice}
            finalTotal={finalTotal}
            orderData={orderData}
            shippingPrices={shippingPrices}
            selectedGovernorate={selectedGovernorate}
            paymentMethod={paymentMethod}
            paymentScreenshot={paymentScreenshot}
            loading={loading}
            cartCount={cartCount}
            handleSubmit={handleSubmit}
            onBack={handleBack}
            hasAllFreeShipping={hasAllFreeShipping}
          />
        );
      default:
        return <div>Unknown step</div>;
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Paper
        sx={{
          p: { xs: 3, md: 4 },
          maxWidth: 800,
          margin: '20px auto',
          borderRadius: 3,
          boxShadow: 3
        }}
      >
        <Box sx={{ 
          bgcolor: 'primary.main', 
          color: 'white', 
          p: 2, 
          borderRadius: 1,
          mb: 3,  // Increased bottom margin for more space
          mx: -3,
          mt: -3
        }}>
          <Typography variant="h6" sx={{ fontWeight: 700, textAlign: 'center' }}>
            إستكمال الطلب
          </Typography>
        </Box>

        <Stepper 
          activeStep={activeStep} 
          alternativeLabel 
          sx={{ 
            mb: 4, 
            flexDirection: 'row-reverse',
            mt: 2 // Added top margin for extra spacing
          }}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}

        {renderStepContent(activeStep)}
      </Paper>
    </Box>
  );
};

export default Checkout;
